// Port of Networking/Connectivity.swift: "should we even try the network?", answered synchronously.

/**
 * `false` means the device is known to be offline (or the user forced offline mode): clients fail fast with
 * `offline` instead of waiting for a timeout. `true` means a request may be attempted; the network remains the judge.
 */
export interface ConnectivityProviding {
  readonly isOnline: boolean;
}

/** Fixed connectivity, for tests and previews. */
export const StaticConnectivity = {
  online: { isOnline: true } as ConnectivityProviding,
  offline: { isOnline: false } as ConnectivityProviding,
  of(isOnline: boolean): ConnectivityProviding {
    return { isOnline };
  },
};

/** Network state as reported by the browser. */
export type ConnectivityStatus =
  /** Not determined yet (monitor not started). Not treated as offline. */
  | { kind: 'unknown' }
  /** No usable network (`navigator.onLine` false). */
  | { kind: 'offline' }
  /** The browser reports a network. `isExpensive`: cellular (Network Information API, where available).
   *  `isConstrained`: the user asked to save data (`navigator.connection.saveData`). */
  | { kind: 'online'; isExpensive: boolean; isConstrained: boolean };

export function sameConnectivityStatus(a: ConnectivityStatus, b: ConnectivityStatus): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'online' && b.kind === 'online') {
    return a.isExpensive === b.isExpensive && a.isConstrained === b.isConstrained;
  }
  return true;
}

export function isExpensiveStatus(status: ConnectivityStatus): boolean {
  return status.kind === 'online' && status.isExpensive;
}

export function isConstrainedStatus(status: ConnectivityStatus): boolean {
  return status.kind === 'online' && status.isConstrained;
}

/** The part of `navigator` the monitor reads. */
export interface NavigatorConnectivity {
  readonly onLine: boolean;
  readonly connection?: { readonly type?: string; readonly saveData?: boolean } & Partial<EventTarget>;
}

/** Maps the browser's view to a status (Swift `ConnectivityStatus(pathStatus:…)`). */
export function connectivityStatusFrom(nav: NavigatorConnectivity): ConnectivityStatus {
  if (!nav.onLine) return { kind: 'offline' };
  const connection = nav.connection;
  return { kind: 'online', isExpensive: connection?.type === 'cellular', isConstrained: connection?.saveData === true };
}
